/**
 * Yearly Demand, grouped by product line. Two views of the same numbers:
 *   Numbers — the workbook grid: each fiscal year (with "sold alone" under it), this year to date, projected.
 *   Chart   — one labelled column chart per line: the total on top of each bar, the year under it; each bar
 *             split into sold on its own (dark) and used inside umbrellas (light); this year so far striped,
 *             the projected year as an outline. Plus last year's change in words.
 * Clicking a SKU's year opens "how this number is built" (BreakdownPanel).
 */
import { useMemo, useState } from "react";
import type { DemandTableRow } from "@march/shared";
import { fmtNum } from "../lib/csv.ts";
import { partName } from "../lib/skuNames.ts";

export type View = "overview" | "graph" | "numbers" | "chart";

export type Totals = { used: Record<string, number>; alone: Record<string, number>; proj: number | null; projCount: number };

const short = (fy: string) => fy.replace(/^FY20/, "FY");

/** A projection below what's already been used this year (or negative) can't be right: show none. */
export function validProj(r: DemandTableRow, currentFy: string): number | null {
  const v = r.fy_projection?.value;
  if (v == null) return null;
  const toDate = r.fy_totals[currentFy] ?? 0;
  return v > 0 && v >= toDate ? v : null;
}

export function totalsOf(rows: DemandTableRow[], fys: string[], currentFy: string): Totals {
  const used: Record<string, number> = {};
  const alone: Record<string, number> = {};
  let proj = 0;
  let projCount = 0;
  for (const r of rows) {
    for (const fy of fys) {
      used[fy] = (used[fy] ?? 0) + (r.fy_totals[fy] ?? 0);
      alone[fy] = (alone[fy] ?? 0) + (r.fy_standalone?.[fy] ?? 0);
    }
    const p = validProj(r, currentFy);
    if (p != null) {
      proj += p;
      projCount++;
    }
  }
  return { used, alone, proj: projCount ? proj : null, projCount };
}

export function change(t: Totals, fys: string[], currentFy: string): { cls: string; text: string; title: string } | null {
  const full = fys.filter((f) => f !== currentFy);
  const [prev, last] = full.slice(-2);
  if (!prev || !last) return null;
  const a = t.used[prev] ?? 0;
  const b = t.used[last] ?? 0;
  const title = `${last} vs ${prev}: ${fmtNum(Math.round(b))} vs ${fmtNum(Math.round(a))}`;
  if (a === 0) return b > 0 ? { cls: "new", text: "new", title } : null;
  const p = Math.round(((b - a) / a) * 100);
  if (Math.abs(p) < 5) return { cls: "flat", text: "about the same", title };
  return { cls: p > 0 ? "up" : "down", text: `${p > 0 ? "▲" : "▼"} ${Math.abs(p)}%`, title };
}

export function GroupedDemand({
  rows, fys, currentFy, category, view, groupOf, onBreakdown, emptyMessage, allOpen = false,
}: {
  rows: DemandTableRow[];
  fys: string[];
  currentFy: string;
  category: string;
  view: View;
  groupOf: (itemid: string) => { key: string; label: string };
  onBreakdown: (itemid: string, fy: string) => void;
  emptyMessage: string;
  /** Every line open, no expand/collapse (the "Every part" grid). */
  allOpen?: boolean;
}) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const yd = category === "Trim";
  const fullFys = fys.filter((f) => f !== currentFy).slice(-3);
  const showFys = [...fullFys, ...(fys.includes(currentFy) ? [currentFy] : [])];

  const groups = useMemo(() => {
    const m = new Map<string, { label: string; rows: DemandTableRow[] }>();
    for (const r of rows) {
      const g = groupOf(r.itemid);
      if (!m.has(g.key)) m.set(g.key, { label: g.label, rows: [] });
      m.get(g.key)!.rows.push(r);
    }
    return [...m.entries()];
  }, [rows, groupOf]);

  if (!rows.length) return <div className="tablewrap"><div className="u-empty">{emptyMessage}</div></div>;
  const flat = groups.length === 1;
  const setAll = (on: boolean) => setOpen(Object.fromEntries(groups.map(([k]) => [k, on])));

  const Cells = ({ t, itemid }: { t: Totals; itemid?: string }) => {
    if (view === "chart") {
      const chg = change(t, fys, currentFy);
      return (
        <>
          <td className="l"><YearChart t={t} fys={showFys} currentFy={currentFy} yd={yd} onBar={itemid ? (fy) => onBreakdown(itemid, fy) : undefined} /></td>
          <td>{chg ? <span className={`ychg ${chg.cls}`} title={chg.title}>{chg.text}</span> : <span className="muted">—</span>}</td>
        </>
      );
    }
    return (
      <>
        {showFys.map((fy) => {
          const v = Math.round(t.used[fy] ?? 0);
          const alone = Math.round(t.alone[fy] ?? 0);
          const cur = fy === currentFy;
          if (!v) return <td key={fy} className="muted">—</td>;
          const body = (
            <>
              <span className={cur ? "muted" : ""}>{fmtNum(v)}</span>
              <span className="alone">{fmtNum(alone)} sold alone</span>
            </>
          );
          return (
            <td key={fy}>
              {itemid ? (
                <button type="button" className="ycell" onClick={() => onBreakdown(itemid, fy)} title={`${fmtNum(v)} used in ${fy}: ${fmtNum(alone)} sold on their own, ${fmtNum(v - alone)} inside umbrellas. Click to see how it's built.`}>
                  {body}
                </button>
              ) : body}
            </td>
          );
        })}
        <td>{t.proj != null ? <b className="muted" title={itemid ? "Straight-line trend of the last 3 full years (the planning workbook's FORECAST.LINEAR)." : `Sum of ${t.projCount} SKU projections`}>{fmtNum(t.proj)}</b> : <span className="muted" title="Can't project: needs 3 full years, or sales are falling too fast">—</span>}</td>
      </>
    );
  };

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 8 }}>
        {view === "chart" ? (
          <span className="yc-legend">
            <span><i className="k-alone" />sold on its own</span>
            <span><i className="k-in" />inside umbrellas</span>
            <span><i className="k-cur" />{short(currentFy)} so far</span>
            <span><i className="k-proj" />{short(currentFy)} projected</span>
          </span>
        ) : (
          <span className="muted" style={{ fontSize: 12.5 }}>Units used per fiscal year (Jul–Jun). "Sold alone" is what NetSuite's Sales by Item shows.</span>
        )}
        <span className="spacer" />
        {!flat && !allOpen && (
          <>
            <button type="button" className="btn" onClick={() => setAll(true)}>Expand all</button>
            <button type="button" className="btn" onClick={() => setAll(false)}>Collapse all</button>
          </>
        )}
      </div>
      <div className="tablewrap">
        <table className={`dt demand ${view}`}>
          <thead>
            <tr>
              <th>{flat ? "SKU" : "Product line / SKU"}</th>
              {view === "chart" ? (
                <>
                  <th className="l">Units used per fiscal year</th>
                  <th>{fullFys.length >= 2 ? `${short(fullFys[fullFys.length - 1])} vs ${short(fullFys[fullFys.length - 2])}` : "Change"}</th>
                </>
              ) : (
                <>
                  {showFys.map((fy) => <th key={fy}>{fy === currentFy ? `${fy} so far` : fy}</th>)}
                  <th title="Straight-line trend of the last 3 full years">{currentFy} projected</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {groups.map(([key, g]) => {
              const single = g.rows.length === 1 || flat;
              const isOpen = flat || allOpen || (open[key] ?? false);
              const t = totalsOf(g.rows, fys, currentFy);
              return (
                <Frag key={key}>
                  {!single && (
                    <tr className="grp click" onClick={() => setOpen((o) => ({ ...o, [key]: !isOpen }))} aria-expanded={isOpen}>
                      <td className="l"><span className="caret">{isOpen ? "▾" : "▸"}</span>{g.label} <span className="sub">· {g.rows.length} SKUs</span></td>
                      <Cells t={t} />
                    </tr>
                  )}
                  {(isOpen || single) &&
                    g.rows.map((r) => (
                      <tr key={r.itemid}>
                        <td className={`l${single ? "" : " ind"}`}>
                          <span className="code">{r.itemid}</span>
                          {partName(r.itemid) && <span className="name">{partName(r.itemid)}</span>}
                        </td>
                        <Cells t={totalsOf([r], fys, currentFy)} itemid={r.itemid} />
                      </tr>
                    ))}
                </Frag>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Frag({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

/** Labelled mini column chart: one bar per year (number on top, year under), plus this year's projection. */
export function YearChart({ t, fys, currentFy, yd, onBar }: { t: Totals; fys: string[]; currentFy: string; yd: boolean; onBar?: (fy: string) => void }) {
  const vals = fys.map((fy) => t.used[fy] ?? 0);
  const max = Math.max(...vals, t.proj ?? 0, 1);
  const H = 44;
  const h = (v: number) => (v > 0 ? Math.max(2, (v / max) * H) : 0);
  return (
    <span className="yc">
      {fys.map((fy, i) => {
        const used = vals[i];
        const alone = Math.min(used, t.alone[fy] ?? 0);
        const cur = fy === currentFy;
        const title = `${fy}${cur ? " so far" : ""}: ${fmtNum(Math.round(used))}${yd ? " yd" : ""} used (${fmtNum(Math.round(alone))} sold on its own, ${fmtNum(Math.round(used - alone))} inside umbrellas)${onBar ? ". Click to see how it's built." : ""}`;
        const Bar = onBar ? "button" : "span";
        return (
          <Bar key={fy} type={onBar ? "button" : undefined} className={`yc-col${cur ? " cur" : ""}`} title={title} onClick={onBar ? () => onBar(fy) : undefined}>
            <b>{used ? fmtNum(Math.round(used)) : "—"}</b>
            <span className="yc-bar" style={{ height: h(used) }}>
              <i className="in" style={{ height: used ? `${((used - alone) / used) * 100}%` : 0 }} />
              <i className="alone" style={{ height: used ? `${(alone / used) * 100}%` : 0 }} />
            </span>
            <small>{short(fy)}{cur ? " so far" : ""}</small>
          </Bar>
        );
      })}
      {t.proj != null && (
        <span className="yc-col proj" title={`${currentFy} projected: ${fmtNum(Math.round(t.proj))} (straight-line trend of the last 3 full years)`}>
          <b>{fmtNum(Math.round(t.proj))}</b>
          <span className="yc-bar" style={{ height: h(t.proj) }} />
          <small>{short(currentFy)} proj.</small>
        </span>
      )}
    </span>
  );
}
